
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle
} from "@/components/ui/card";
import {
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  ResponsiveContainer,
  Legend,
  Tooltip
} from "recharts";
import type { RadarDataPoint } from "./types";

interface PerformanceMetricsComparisonChartProps {
  data: RadarDataPoint[];
}

// Define colors for consistency
const RECRUITER_COLORS = [
  "#9b87f5", // Purple
  "#7E69AB", // Dark Purple
  "#33C3F0", // Blue
  "#F97316"  // Orange
];

export const PerformanceMetricsComparisonChart = ({ data }: PerformanceMetricsComparisonChartProps) => {
  // Transform data for radar chart
  // Each RadarDataPoint has the recruiter name and metrics like "Hire Rate", etc.
  // The RadarChart expects each data point to represent a metric axis
  
  // Extract metrics keys (excluding the name property)
  const metricsKeys = Object.keys(data[0] || {}).filter(key => key !== 'name');
  
  // Transform data for radar chart format
  // From: [{name: "Recruiter1", "Metric1": val1, "Metric2": val2}, {name: "Recruiter2", ...}]
  // To: [{metric: "Metric1", "Recruiter1": val1, "Recruiter2": val2}, {metric: "Metric2", ...}]
  const transformedData = metricsKeys.map(metric => {
    const metricData: Record<string, any> = { name: metric };
    
    // Add each recruiter's value for this metric
    data.forEach(recruiter => {
      metricData[recruiter.name] = recruiter[metric as keyof RadarDataPoint];
    });
    
    return metricData;
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>Performance Metrics Comparison</CardTitle>
        <CardDescription>Multi-dimensional performance analysis</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <RadarChart outerRadius={90} data={transformedData}>
              <PolarGrid />
              <PolarAngleAxis dataKey="name" />
              <PolarRadiusAxis angle={30} domain={[0, 100]} />
              
              {/* Dynamically create a Radar for each recruiter */}
              {data.map((recruiter, index) => (
                <Radar
                  key={recruiter.name}
                  name={recruiter.name}
                  dataKey={recruiter.name}
                  stroke={RECRUITER_COLORS[index % RECRUITER_COLORS.length]}
                  fill={RECRUITER_COLORS[index % RECRUITER_COLORS.length]}
                  fillOpacity={0.6}
                />
              ))}
              
              <Legend />
              <Tooltip />
            </RadarChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
};
